import { Button, Tooltip } from '@mui/material';
import { useMemo, useState } from 'react';
import { CourseModuleTypeDto } from '../../../../generated-types/courseModuleTypeDto';
import BaseNavButtonProps from '../../lib/BaseNavButtonProps';

/**
 * The next button properties
 */
type NextButtonProps = {
  /**
   * A method to move to the next module
   */
  moveToNextModule: () => void;
} & BaseNavButtonProps;

/**
 * The next button for course navigation
 */
const NextButton = (props: NextButtonProps) => {
  const [nextButtonTooltip, setNextButtonTooltip] = useState('');
  const onLastModule = useMemo(
    () => props.selectedCourseModuleIndex + 1 == props.courseModules.length,
    [props.courseModules, props.selectedCourseModuleIndex]
  );

  /**
   * Determine whether the next button should be disabled
   */
  const nextButtonDisabled = useMemo(() => {
    const defaultNextTooltip = 'Next Module';
    // If we're on the last module, it's automatically disabled
    if (onLastModule) {
      setNextButtonTooltip(
        props.completeStates[props.selectedCourseModuleIndex]
          ? 'Course Complete'
          : 'On Last Module'
      );
      return true;
    }

    // If the current module is complete, we can always enable the next button
    if (props.completeStates[props.selectedCourseModuleIndex]) {
      setNextButtonTooltip(defaultNextTooltip);
      return false;
    }

    // If the next module is not complete, that means we found the user's current module
    if (!props.completeStates[props.selectedCourseModuleIndex + 1]) {
      switch (props.courseModules[props.selectedCourseModuleIndex].moduleType) {
        case CourseModuleTypeDto.Quiz:
          setNextButtonTooltip('Complete quiz to continue');
          break;
        case CourseModuleTypeDto.Video:
          setNextButtonTooltip('Watch entire video to continue');
          break;
        case CourseModuleTypeDto.Introduction:
        case CourseModuleTypeDto.Complete:
        case CourseModuleTypeDto.Survey:
        default:
          setNextButtonTooltip('Complete the module to continue');
          break;
      }

      return true;
    }

    setNextButtonTooltip(defaultNextTooltip);
    return false;
  }, [
    onLastModule,
    props.completeStates,
    props.selectedCourseModuleIndex,
    props.courseModules,
  ]);

  return (
    <Tooltip title={nextButtonTooltip} arrow>
      <span
        style={{
          width: props.width,
          cursor: nextButtonDisabled ? 'help' : 'pointer',
        }}
      >
        <Button
          variant="contained"
          sx={{
            height: props.height,
            width: '100%',
          }}
          disabled={nextButtonDisabled}
          onClick={props.moveToNextModule}
          data-testid="module-nav-next-button"
        >
          {onLastModule
            ? props.completeStates[props.selectedCourseModuleIndex]
              ? 'Complete'
              : 'Last Module'
            : 'Next'}
        </Button>
      </span>
    </Tooltip>
  );
};

export default NextButton;
